import MonthRangeSelector from '@/components/MonthRangeSelector'
import Paper from '@/components/Paper'
import { CHART_COLORS } from '@/constants/chartColors'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { Calendar } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { getServiceBreakdown } from '../../../services'
import { SalesTargetFilters } from '../../../types'
import RevenueBreakdownChart from './RevenueBreakdownChart'
import RevenueShareChart from './RevenueShareChart'
import ServiceBreakdownTable from './ServiceBreakdownTable'

interface ServiceBreakdownProps {
    filters: SalesTargetFilters
}

const COLORS = [
    CHART_COLORS.PRIMARY_BLUE,
    CHART_COLORS.EMERALD_GREEN,
    CHART_COLORS.AMBER_ORANGE,
    CHART_COLORS.INDIGO_PURPLE,
    CHART_COLORS.PINK_FUCHSIA,
    CHART_COLORS.BLUE_GRAY,
    CHART_COLORS.ROSE_RED,
]

const ServiceBreakdown = ({ filters }: ServiceBreakdownProps) => {
    const { t } = useTranslation()
    const [compareRange, setCompareRange] = useState<{
        gte: string | null
        lte: string | null
    }>({ gte: null, lte: null })
    const [showCompareSelector, setShowCompareSelector] = useState(false)

    const hasComparison = !!compareRange.gte && !!compareRange.lte

    const handleAddComparison = () => {
        if (filters.gte && filters.lte) {
            const gteDate = dayjs(filters.gte)
            const lteDate = dayjs(filters.lte)
            setCompareRange({
                gte: gteDate.subtract(1, 'year').format('YYYY-MM-DD'),
                lte: lteDate.subtract(1, 'year').format('YYYY-MM-DD'),
            })
            setShowCompareSelector(true)
        }
    }

    const handleRemoveComparison = () => {
        setCompareRange({ gte: null, lte: null })
        setShowCompareSelector(false)
    }

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'service-breakdown',
            filters.gte,
            filters.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getServiceBreakdown({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Comparison data
    const { data: comparisonData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'service-breakdown-comparison',
            compareRange.gte,
            compareRange.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getServiceBreakdown({
                gte: compareRange.gte!,
                lte: compareRange.lte!,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: hasComparison,
    })

    // Period labels
    const currentPeriodLabel = useMemo(() => {
        if (!filters.gte || !filters.lte) return ''
        return `${dayjs(filters.gte).format('MM/YYYY')} - ${dayjs(filters.lte).format('MM/YYYY')}`
    }, [filters.gte, filters.lte])

    const comparePeriodLabel = useMemo(() => {
        if (!compareRange.gte || !compareRange.lte) return ''
        return `${dayjs(compareRange.gte).format('MM/YYYY')} - ${dayjs(compareRange.lte).format('MM/YYYY')}`
    }, [compareRange.gte, compareRange.lte])

    // Sort data by total_revenue descending
    const sortedData = useMemo(() => {
        if (!data || !Array.isArray(data)) return []
        return [...data].sort(
            (a: any, b: any) => b.total_revenue - a.total_revenue
        )
    }, [data])

    // Sort comparison data by total_revenue descending
    const sortedComparisonData = useMemo(() => {
        if (!comparisonData || !Array.isArray(comparisonData)) return []
        return [...comparisonData].sort(
            (a: any, b: any) => b.total_revenue - a.total_revenue
        )
    }, [comparisonData])

    // Prepare data for donut chart - Revenue share by service
    const revenueShareData = useMemo(() => {
        if (!sortedData || sortedData.length === 0) return []
        return sortedData.map((service: any) => ({
            name: service.product_group_name || 'N/A',
            value: service.total_revenue,
            share: service.revenue_share,
        }))
    }, [sortedData])

    // Comparison donut chart data
    const comparisonRevenueShareData = useMemo(() => {
        if (!sortedComparisonData || sortedComparisonData.length === 0)
            return []
        return sortedComparisonData.map((service: any) => ({
            name: service.product_group_name || 'N/A',
            value: service.total_revenue,
            share: service.revenue_share,
        }))
    }, [sortedComparisonData])

    // Prepare data for stacked bar chart - Revenue breakdown by type
    const revenueBreakdownData = useMemo(() => {
        if (!sortedData || sortedData.length === 0) return []
        return sortedData.map((service: any) => ({
            name: service.product_group_name || 'N/A',
            renewal: service.renewal_revenue,
            newSale: service.new_sale_revenue,
            upsell: service.upsell_revenue,
        }))
    }, [sortedData])

    // Combined data for grouped bar chart comparison
    const combinedBreakdownData = useMemo(() => {
        if (!sortedData || sortedData.length === 0) return []

        return sortedData.map((service: any, index: number) => {
            const compService = sortedComparisonData?.find(
                (c: any) => c.product_group_name === service.product_group_name
            )
            const currentTotal =
                (service.renewal_revenue || 0) +
                (service.new_sale_revenue || 0) +
                (service.upsell_revenue || 0)
            const compTotal = compService
                ? (compService.renewal_revenue || 0) +
                  (compService.new_sale_revenue || 0) +
                  (compService.upsell_revenue || 0)
                : 0

            return {
                name: service.product_group_name || 'N/A',
                current: currentTotal,
                comparison: compTotal,
                color: COLORS[index % COLORS.length],
            }
        })
    }, [sortedData, sortedComparisonData])

    // Find top growing services
    const topGrowthServices = useMemo(() => {
        if (!data || !Array.isArray(data))
            return { upsell: null, newSale: null }

        const sortedByUpsell = [...data].sort(
            (a: any, b: any) => b.upsell_revenue - a.upsell_revenue
        )
        const sortedByNewSale = [...data].sort(
            (a: any, b: any) => b.new_sale_revenue - a.new_sale_revenue
        )

        return {
            upsell: sortedByUpsell[0],
            newSale: sortedByNewSale[0],
        }
    }, [data])

    const renderComponent = renderState()
    if (renderComponent) return renderComponent

    return (
        <Paper className="space-y-2">
            <div className="flex items-center justify-between">
                <Typogaphy size="medium" className="font-medium">
                    {t('employees.performance.service_breakdown')}
                </Typogaphy>

                {/* Comparison selector */}
                <div className="flex items-center gap-2">
                    {!showCompareSelector ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleAddComparison}
                        >
                            <Calendar className="w-4 h-4 mr-1" />
                            {t('common.compare')}
                        </Button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <MonthRangeSelector
                                value={
                                    compareRange.gte && compareRange.lte
                                        ? {
                                              gte: compareRange.gte,
                                              lte: compareRange.lte,
                                          }
                                        : undefined
                                }
                                onChange={(value) =>
                                    setCompareRange({
                                        gte: value?.gte || null,
                                        lte: value?.lte || null,
                                    })
                                }
                            />
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleRemoveComparison}
                                className="!text-red-500"
                            >
                                {t('common.cancel')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {hasComparison && (
                <div className="px-4 py-2 bg-gray-50 rounded text-sm text-gray-600">
                    <span className="font-medium">{currentPeriodLabel}</span>
                    <span className="mx-2">so với</span>
                    <span className="font-medium text-gray-500">
                        {comparePeriodLabel}
                    </span>
                </div>
            )}

            {/* Top insights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {topGrowthServices.upsell && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                        <Typogaphy size="small" className="text-gray-600 mb-1">
                            {t(
                                'employees.performance.upsell_strongest_service'
                            )}
                        </Typogaphy>
                        <Typogaphy className="font-semibold text-amber-800 text-lg">
                            {topGrowthServices.upsell.product_group_name ||
                                'N/A'}
                        </Typogaphy>
                        <Typogaphy size="small" className="text-gray-700 mt-1">
                            {formatCurrency(
                                topGrowthServices.upsell.upsell_revenue
                            )}
                            {topGrowthServices.upsell.upsell_growth_rate >
                                0 && (
                                <span className="text-green-600 ml-2">
                                    +
                                    {formatNumeral(
                                        topGrowthServices.upsell
                                            .upsell_growth_rate
                                    )}
                                    %
                                </span>
                            )}
                        </Typogaphy>
                    </div>
                )}

                {topGrowthServices.newSale && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                        <Typogaphy size="small" className="text-gray-600 mb-1">
                            {t(
                                'employees.performance.new_sale_strongest_service'
                            )}
                        </Typogaphy>
                        <Typogaphy className="font-semibold text-blue-800 text-lg">
                            {topGrowthServices.newSale.product_group_name ||
                                'N/A'}
                        </Typogaphy>
                        <Typogaphy size="small" className="text-gray-700 mt-1">
                            {formatCurrency(
                                topGrowthServices.newSale.new_sale_revenue
                            )}
                            {topGrowthServices.newSale.new_sale_growth_rate >
                                0 && (
                                <span className="text-green-600 ml-2">
                                    +
                                    {formatNumeral(
                                        topGrowthServices.newSale
                                            .new_sale_growth_rate
                                    )}
                                    %
                                </span>
                            )}
                        </Typogaphy>
                    </div>
                )}
            </div>

            {/* Donut Charts - Revenue Share */}
            <RevenueShareChart
                data={revenueShareData}
                comparisonData={comparisonRevenueShareData}
                currentPeriodLabel={currentPeriodLabel}
                comparePeriodLabel={comparePeriodLabel}
                hasComparison={hasComparison}
            />

            {/* Bar Charts */}
            <RevenueBreakdownChart
                data={revenueBreakdownData}
                combinedData={combinedBreakdownData}
                currentPeriodLabel={currentPeriodLabel}
                comparePeriodLabel={comparePeriodLabel}
                hasComparison={hasComparison}
            />

            {/* Current period table */}
            <ServiceBreakdownTable
                data={sortedData}
                comparisonData={sortedComparisonData}
                periodLabel={currentPeriodLabel}
                hasComparison={hasComparison}
            />

            {/* Comparison period table */}
            {hasComparison && sortedComparisonData.length > 0 && (
                <ServiceBreakdownTable
                    data={sortedComparisonData}
                    periodLabel={comparePeriodLabel}
                    hasComparison={hasComparison}
                    isComparisonTable
                />
            )}
        </Paper>
    )
}

export default ServiceBreakdown
